import { Box, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"

import { Section, SectionList, SectionTitle, Text } from "@jewlr/storybook/info"
import { jewlr_t } from "helpers/application"
import { cn } from "styles/utils"

const SubSectionTitle = ({ className, ...props }) => (
  <SectionTitle
    as="h3"
    className={cn(
      "text-14 tablet:text-18 font-medium tracking-normal",
      className
    )}
    {...props}
  />
)

SubSectionTitle.propTypes = {
  className: PropTypes.string,
}

const PrivacyNotice = () => (
  <Box mb={7}>
    <Section>
      <Box contain>
        <SectionTitle>
          WHAT PERSONAL INFORMATION ABOUT CUSTOMERS DOES JEWLR COLLECT?
        </SectionTitle>
        <Text>
          We collect your personal information in order to provide and
          continually improve our products and services. Here are the types of
          information we gather:
        </Text>
      </Box>
    </Section>
    <Section className="tablet:py-32 py-16" hasBg>
      <Box contain>
        <SectionList>
          <li>
            {" "}
            <SubSectionTitle>Information You Give Us:</SubSectionTitle>
            <Text className="tablet:pb-32 pb-16">
              we receive and store any information you provide in relation to
              your order or any service you request. We collect contact
              information so that we can contact you for your service and
              delivery of your purchased products to you. You can choose not to
              provide certain information but then you might not be able to take
              advantage of many of our services.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>Automatic Information:</SubSectionTitle>
            <Text className="tablet:pb-32 pb-16">
              we automatically receive and store certain types of information
              when you use Jewlr, such as information about your use, including
              your interaction with product and services available. Like many
              websites, we use cookies and other unique identifiers and we
              obtain certain types of information when your web browser or
              device accesses Jewlr websites (i.e. Jewlr.com, Jewlr.co.uk,
              Jewlr.ca, etc.) and other content served by or on behalf of Jewlr.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>Information From Other Sources:</SubSectionTitle>
            <Text>
              We might receive information about you from other sources.
              Examples of information we receive from other sources include
              updated delivery and address information from our carriers.
            </Text>
          </li>
        </SectionList>
      </Box>
    </Section>
    <Section>
      <Box contain>
        <SectionTitle>
          FOR WHAT PURPOSES DOES JEWLR PROCESS YOUR PERSONAL INFORMATION?
        </SectionTitle>
        <Text className="tablet:pb-24 pb-0">
          We process your personal information to operate, provide, and improve
          Jewlr offerings to our customers. These purposes include:
        </Text>
        <SectionList>
          <li>
            {" "}
            <SubSectionTitle>
              Provide, troubleshoot, and improve.
            </SubSectionTitle>
            <Text className="tablet:pb-32 pb-16">
              We use your personal information to provide functionality, analyse
              performance, fix errors, and improve usability and effectiveness
              of our products and services.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>
              Recommendations and personalisation.
            </SubSectionTitle>
            <Text className="tablet:pb-32 pb-16">
              We use your personal information to recommend features, products,
              and services that might be of interest to you, identify your
              preferences, and personalise your experience with Jewlr.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>Comply with legal obligations.</SubSectionTitle>
            <Text className="tablet:pb-32 pb-16">
              In certain cases, we have a legal obligation to collect and
              process your personal information.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>Advertising.</SubSectionTitle>
            <Text className="tablet:pb-32 pb-16">
              We use your personal information to display interest-based ads for
              features, products, and services that might be of interest to you.
              To serve you interest-based ads, we use information such as your
              interactions with Jewlr sites, content, products or services. We
              do not use personally identifiable information such as name or
              e-mail to serve interest-based ads. As is common in the
              advertising industry, we use cookies, pixels, and other
              technologies (collectively, &quot;cookies&quot;), which enable us
              to understand the effectiveness of the interest-based ads we show
              you by measuring what ads are clicked or viewed, and to provide
              you with more useful and relevant ads. For example, if we know
              what ads are shown to your browser we can be careful not to show
              the same ads repeatedly.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>
              Fraud Prevention and Credit Risks.
            </SubSectionTitle>
            <Text className="tablet:pb-32 pb-16">
              We process personal information to prevent and detect fraud and
              abuse in order to protect the security of our customers, and
              Jewlr, and others. We may also use scoring methods to assess and
              manage credit risks.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>
              Purposes for which we seek your consent.
            </SubSectionTitle>
            <Text>
              We may also ask for your consent to process your personal
              information for a specific purpose that we communicate to you.
              When you consent to our processing your personal information for a
              specified purpose, you may withdraw your consent at any time and
              we will stop processing of your data for that purpose.
            </Text>
          </li>
        </SectionList>
      </Box>
    </Section>
    <Section hasBg>
      <Box contain>
        <SectionTitle>WHAT ABOUT COOKIES?</SectionTitle>
        <Text className="tablet:pb-32 pb-16">
          To enable our systems to recognise your browser or device and to
          provide services to you, we use cookies. Please read more about
          cookies in our{" "}
          <Link data-lc="privacy-notice" to="/info/privacy-policy">
            Privacy Policy
          </Link>
        </Text>
        <SectionTitle>DOES JEWLR SHARE YOUR PERSONAL INFORMATION?</SectionTitle>
        <Text className="tablet:pb-24 pb-0">
          Information about our customers is an important part of our business
          and we are not in the business of selling personal information to
          others. Jewlr shares information only as described below and with the
          subsidiaries that Jewlr controls that are either subject to this
          Privacy Notice or follow practices at least as protective as those
          described in this Privacy Notice.
        </Text>
        <SectionList>
          <li>
            {" "}
            <SubSectionTitle>Third Party Service Providers:</SubSectionTitle>
            <Text className="tablet:pb-32 pb-24">
              We employ other companies and individuals to perform functions on
              our behalf. Examples include fulfilling orders for products or
              services, delivering packages, sending postal mail and e-mail,
              removing repetitive information from customer lists, analysing
              data, providing marketing assistance, providing search results and
              links (including paid listings and links), processing payments,
              transmitting content, scoring credit risk, and providing customer
              service. These third-party service providers have access to
              personal information needed to perform their functions, but may
              not use it for other purposes. Further, they must process the
              personal information in accordance with this{" "}
              <strong>Privacy Notice</strong> and as permitted by applicable
              data protection laws.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>Business Transfers:</SubSectionTitle>
            <Text className="tablet:pb-32 pb-24">
              As we continue to develop our business, we might sell or buy other
              businesses or services. In such transactions, customer information
              generally is one of the transferred business assets but remains
              subject to the promises made in any pre-existing Privacy Notice
              (unless, of course, the customer consents otherwise). Also, in the
              unlikely event that Jewlr.com. or substantially all of its assets
              are acquired, customer information will of course be one of the
              transferred assets.
            </Text>
          </li>
          <li>
            {" "}
            <SubSectionTitle>Protection of Jewlr and Others:</SubSectionTitle>
            <Text className="tablet:pb-32 pb-24">
              We release account and other personal information when we believe
              release is appropriate to comply with the law; enforce or apply
              our{" "}
              <Link data-lc="privacy-notice" to="/info/terms-conditions">
                Terms and Conditions{" "}
              </Link>
            </Text>
          </li>
          <li>
            <Text className="tablet:pb-32 pb-24">
              Other than as set out above, you will receive notice when personal
              information about you might be shared with third parties and you
              will have an opportunity to choose not to share the information.
            </Text>
          </li>
        </SectionList>
        <Text>
          <strong>Transfers out of the European Economic Area.</strong> Whenever
          we transfer personal information to countries outside of the European
          Economic Area in the course of sharing information as set out above,
          we will ensure that the information is transferred in accordance with
          this Privacy Notice and as permitted by the applicable laws on data
          protection.
        </Text>
      </Box>
    </Section>
    <Section>
      <Box contain>
        <SectionTitle>HOW SECURE IS INFORMATION ABOUT ME?</SectionTitle>
        <Text>
          We design our systems with your security and privacy in mind.
        </Text>
        <SectionList>
          <li>
            <Text>
              We work to protect the security of your information during
              transmission by using Secure Sockets Layer (SSL) software, which
              encrypts information you input.
            </Text>
          </li>
          <li>
            <Text>
              We follow the Payment Card Industry Data Security Standard (PCI
              DSS) when handling credit card data.
            </Text>
          </li>
          <li>
            <Text>
              We maintain physical, electronic and procedural safeguards in
              connection with the collection, storage and disclosure of personal
              customer information. Our security procedures mean that we may
              occasionally request proof of identity before we disclose personal
              information to you.
            </Text>
          </li>
          <li>
            <Text>
              Our devices offer security features to protect them against
              unauthorised access and loss of data. You can control these
              features and configure them based on your needs.
            </Text>
          </li>
          <li>
            <Text>
              It is important for you to protect against unauthorised access to
              your password and to your computers, devices, and applications. Be
              sure to sign off when you finish using a shared computer.
            </Text>
          </li>
        </SectionList>
      </Box>
    </Section>
    <Section hasBg>
      <Box contain>
        <SectionTitle>
          WHAT ABOUT THIRD-PARTY ADVERTISERS AND LINKS TO OTHER WEBSITES?
        </SectionTitle>
        <Text className="mb-0">
          Jewlr may include third-party advertising and links to other websites
          and apps. Third party advertising partners may collect information
          about you when you interact with their content, advertising, and
          services.
        </Text>
      </Box>
    </Section>
    <Section>
      <Box contain>
        <SectionTitle>WHAT INFORMATION CAN I ACCESS?</SectionTitle>
        <Text>
          You can access your purchase history in the &quot;My Account&quot;
          section of the website.
        </Text>
      </Box>
    </Section>
    <Section hasBg>
      <Box contain>
        <SectionTitle>WHAT CHOICES DO I HAVE?</SectionTitle>
        <Text>
          If you have any questions or objection as to how we collect and
          process your personal information, please send an email to{" "}
          <Typography as="a" href="mailto:privacy@jewlr.com" rel="nofollow">
            privacy@jewlr.com
          </Typography>
          .
        </Text>
        <SectionList>
          <li>
            <Text>
              As described above, you can choose not to provide certain
              information but then you might not be able to take advantage of
              some of our offerings and services.
            </Text>
          </li>
          <li>
            <Text>
              You can add or update certain information on our self service
              portal, on your account page or by contacting customer service.
              When you update information, we usually keep a copy of the
              previous version for our records.
            </Text>
          </li>
          <li>
            <Text>
              If you no longer want to receive e-mail or other communications
              from us, you can opt-out from any email you’ve received from us or
              you can contact customer service and request you be removed from
              that list.
            </Text>
          </li>
          <li>
            <Text>
              The Help feature on most browsers and devices will tell you how to
              prevent your browser or device from accepting new cookies, how to
              have the browser notify you when you receive a new cookie or how
              to disable cookies altogether. Because cookies allow you to take
              advantage of some essential information we recommend that you
              leave them turned on. For instance, if you block or otherwise
              reject our cookies, you will not be able to add items to your{" "}
              {jewlr_t("Cart")}, proceed to Checkout, or use your account page,
              self serve portal or any page that require you to sign in.
            </Text>
          </li>
          <li>
            <Text>
              If you want to browse our websites anonymously, you may do so by
              disabling cookies on your browser.
            </Text>
          </li>
        </SectionList>
        <Text>
          In addition, subject to applicable law, you have the right to request
          access to, correct, and delete your personal data, and to ask for data
          portability. You may also object to our processing of your personal
          data or ask that we restrict the processing of your personal data in
          certain instances. If you wish to do any of these things, please
          contact Customer Service or email{" "}
          <Typography
            as="a"
            color="primary"
            fontSize={16}
            href={`mailto:${sl.config.customer_service_email}`}
            rel="nofollow"
          >
            {sl.config.customer_service_email}{" "}
          </Typography>
          .
        </Text>
      </Box>
    </Section>
    <Section>
      <Box contain>
        <SectionTitle>
          HOW LONG DO WE KEEP YOUR PERSONAL INFORMATION?
        </SectionTitle>
        <Text>
          We keep your personal information to enable your continued use of
          Jewlr , for as long as it is required in order to fulfil the relevant
          purposes described in this Privacy Notice, as may be required by law
          such as for tax and accounting purposes, or as otherwise communicated
          to you. For example, we retain your transaction history so that you
          can review past purchases (and repeat orders if desired) and what
          addresses you have shipped orders to, and to improve the relevancy of
          products and content we recommend.
        </Text>
      </Box>
    </Section>
    <Section hasBg>
      <Box contain>
        <SectionTitle>CONTACTS, NOTICES & REVISIONS</SectionTitle>
        <Text>
          If you have any concern about privacy at Jewlr or want to contact one
          of our data controllers, please contact{" "}
          <Typography as="a" href="mailto:privacy@jewlr.com" rel="nofollow">
            privacy@jewlr.com
          </Typography>{" "}
          with a thorough description and we will try to resolve the issue for
          you.
        </Text>
        <Text>
          Our business changes constantly and our Privacy Notice will change
          also. You should check our website frequently to see recent changes.
          Unless stated otherwise, our current Privacy Notice applies to all
          information that we have about you and your account. We stand behind
          the promises we make, however, and will never materially change our
          policies and practices to make them less protective of customer
          information collected in the past without the consent of affected
          customers.
        </Text>
      </Box>
    </Section>
    <Section>
      <Box contain>
        <SectionTitle>EXAMPLES OF INFORMATION COLLECTED</SectionTitle>
        <Text subtitle>Information You Give Us When You use Jewlr:</Text>
        <SectionList>
          <li>
            <Text>search for products or services;</Text>
          </li>
          <li>
            <Text>place an order on Jewlr;</Text>
          </li>
          <li>
            <Text>
              download, stream, view, or use content on a device, or through a
              service or application on a device;
            </Text>
          </li>
          <li>
            <Text>
              provide information in your account (and you might have more than
              one if you have used more than one e-mail address or mobile number
              when shopping with us)
            </Text>
          </li>
          <li>
            <Text>communicate with us by phone, e-mail, or otherwise;</Text>
          </li>
          <li>
            <Text>
              complete a questionnaire, a support ticket, or a contest entry
              form;
            </Text>
          </li>
          <li>
            <Text>upload images, videos or other files;</Text>
          </li>
          <li>
            <Text>
              participate in community features, provide and rate Customer
              Reviews;
            </Text>
          </li>
          <li>
            <Text>
              employ product status alerts, such as Order Notifications or
              Shipping Notifications.
            </Text>
          </li>
        </SectionList>
        <Text>
          As a result of those actions, you might supply us with such
          information as: your name; address and phone number; payment
          information; your location information; people to whom purchases have
          been dispatched (including addresses and phone numbers); e-mail
          addresses of your friends and other people; content of reviews and
          e-mails to us; personal description and photograph; images and videos
          uploaded or emailed to us, information and documents regarding
          identity and standing; credit history information.
        </Text>
      </Box>
    </Section>
    <Section hasBg>
      <Box contain>
        <SectionTitle>AUTOMATIC INFORMATION</SectionTitle>
        <Text>Examples of the information we collect and analyse include:</Text>

        <SectionList>
          <li>
            <Text>
              the Internet protocol (IP) address used to connect your computer
              to the Internet;
            </Text>
          </li>
          <li>
            <Text>login; e-mail address; password;</Text>
          </li>
          <li>
            <Text>the location of your device or computer;</Text>
          </li>
          <li>
            <Text>
              content interaction information, such as content downloads,
              streams, and playback details network details, including
              information about your internet service provider;
            </Text>
          </li>
          <li>
            <Text>
              Jewlr metrics (e.g., the occurrences of technical errors, your
              interactions with service features and content, your settings
              preferences and backup information, location of your device
              running an application, information about uploaded images and
              files (e.g., file name, dates, times and location of your
              images));
            </Text>
          </li>
          <li>
            <Text>
              purchase and content use history, which we sometimes aggregate
              with similar information from other customers to create features
              such as Best Sellers;
            </Text>
          </li>
          <li>
            <Text>
              the full Uniform Resource Locators (URL) clickstream to, through
              and from our website (including date and time); cookie number;
              products and/ or content you viewed or searched for; page response
              times, download errors, length of visits to certain pages, page
              interaction information (such as scrolling, clicks, and
              mouse-overs),
            </Text>
          </li>
          <li>
            <Text>phone numbers used to call our customer service number.</Text>
          </li>
        </SectionList>
        <Text>
          We may also use device identifiers, cookies, and other technologies on
          devices, applications and our web pages to collect browsing, usage or
          other technical information for fraud prevention purposes.
        </Text>
      </Box>
    </Section>
    <Section>
      <Box contain>
        <SectionTitle>INFORMATION FROM OTHER SOURCES</SectionTitle>
        <Text>
          Examples of information we receive from other sources include:
        </Text>
        <SectionList>
          <li>
            <Text>
              updated delivery and address information from our carriers or
              other third parties, which we use to correct our records and
              deliver your next purchase or communication more easily;
            </Text>
          </li>
        </SectionList>
      </Box>
    </Section>
    <Section hasBg>
      <Box contain>
        <SectionTitle>INFORMATION YOU CAN ACCESS</SectionTitle>
        <Text>
          Examples of information you can access through Jewlr include:
        </Text>
        <SectionList>
          <li>
            <Text>status of recent orders;</Text>
          </li>
          <li>
            <Text>your complete order history;</Text>
          </li>
          <li>
            <Text>
              personally identifiable information (including name, e-mail,
              address, phone);
            </Text>
          </li>
          <li>
            <Text>Wish Lists and {jewlr_t("Cart")} information;</Text>
          </li>
        </SectionList>
      </Box>
    </Section>
    <Section>
      <Box contain>
        <SectionTitle>INTEREST BASED ADS</SectionTitle>
        <SectionList>
          <li>
            <Text>
              To serve you interest-based ads, we use information such as your
              interactions with Jewlr sites, content, products or services. We
              do not use personally identifiable information such as name or
              e-mail to serve interest-based ads. As is common in the
              advertising industry, we use cookies, pixels, and other
              technologies (collectively, &quot;cookies&quot;), which enable us
              to understand the effectiveness of the interest-based ads we show
              you by measuring what ads are clicked or viewed, and to provide
              you with more useful and relevant ads. For example, if we know
              what ads are shown to your browser we can be careful not to show
              the same ads repeatedly.
            </Text>
          </li>
        </SectionList>
      </Box>
    </Section>

    <Section hasBg>
      <Box contain>
        <SectionTitle>CALIFORNIA CONSUMER PRIVACY ACT (CCPA)</SectionTitle>
        <Text>
          This California Consumer Privacy Act disclosure section
          (&ldquo;Disclosure&rdquo;) supplements the Jewlr Privacy Policy and is
          effective as of July 1, 2020.
        </Text>

        <Text>
          The Jewlr Privacy Policy describes the personal information that we
          collect, the sources from which we collect it, the purposes for which
          we use it, the limited circumstances under which we share personal
          information, and with whom we share it. These additional disclosures
          are required by the California Consumer Privacy Act:
        </Text>

        <SubSectionTitle className="pt-24">
          1. Categories of personal information collected
        </SubSectionTitle>
        <Text>
          The personal information that Jewlr collects, or has collected from
          consumers in the twelve months prior to the effective date of this
          Disclosure, fall into the following categories established by the
          California Consumer Privacy Act.
        </Text>
        <SectionList>
          <li>
            identifiers such as your name, alias, address, phone numbers, email,
            or IP address;
          </li>
          <li>
            inference data, such as information about your purchase preferences;
          </li>
          <li>
            geolocation data, such as the location of your device or computer;
          </li>
          <li>purchase activity</li>
          <li>
            visual information such as photos uploaded for photo box or photo
            pendant
          </li>
          <li>
            information such as age, gender, or other protected classifications,
          </li>
          <li>commercial information, such as purchase activity;</li>
          <li>audio or visual information, such as images and videos</li>
        </SectionList>

        <SubSectionTitle className="pt-24">
          2. Categories of personal information NOT collected by Jewlr
        </SubSectionTitle>
        <Text>Jewlr does not collect your credit card number.</Text>

        <SubSectionTitle className="pt-24">
          3. Categories of personal information disclosed for a business purpose
        </SubSectionTitle>
        <Text>
          The personal information that Jewlr disclosed about consumers for a
          business purpose in the twelve months prior to the effective date of
          this Disclosure fall into the following categories established by the
          California Consumer Privacy Act:
        </Text>
        <SectionList>
          <li>
            identifiers such as your name, address, phone numbers, or IP
            address, for example if we use a third-party carrier to deliver your
            order;
          </li>
          <li>
            your age, gender, or other protected classifications, for example if
            you choose to participate in a survey;
          </li>
          <li>
            commercial information, such as the details of a product or service
            you purchased if a third-party service provider is assisting to
            provide that product or service to you;
          </li>
          <li>
            internet or other electronic network activity information, such as
            if we use a service provider to help us manage against fraud;
          </li>
          <li>
            geolocation data, such as providing a delivery partner information
          </li>
          <li>
            audio or visual information, for example if a service provider
            reviews recordings of customer service phone calls for quality
            assurance purposes,
          </li>
        </SectionList>

        <SubSectionTitle className="pt-24">
          4. Right to Request Access to or Deletion of Personal Information
        </SubSectionTitle>
        <Text>
          California residents have a right under the California Consumer
          Privacy Act to request information about the collection of your
          personal information by Jewlr, or access of or deletion of your
          personal information. If you wish to do any of these things, please
          contact{" "}
          <Typography as="a" href="mailto:privacy@jewlr.com" rel="nofollow">
            privacy@jewlr.com
          </Typography>{" "}
          Depending on your data choices, certain services may be limited or
          unavailable. You can make a request one time each year, by emailing
          us. Please mention in your letter that you are making a
          &ldquo;California Shine the Light&rdquo; inquiry. We will respond
          within 30 days.
        </Text>

        <SubSectionTitle className="pt-24">
          5. No sale of personal information
        </SubSectionTitle>
        <Text>
          In the twelve months prior to the effective date of this Disclosure,
          Jewlr has not sold any personal information of consumers, as those
          terms are defined under the California Consumer Privacy Act.
        </Text>

        <SubSectionTitle className="pt-24">
          6. No Discrimination
        </SubSectionTitle>
        <Text>
          Jewlr will not discriminate against any consumer for exercising their
          rights under the California Consumer Privacy Act.
        </Text>
      </Box>
    </Section>
  </Box>
)

export default PrivacyNotice
